// ============================================================================
// HTTP client with classified errors.
// Backend lives at the same origin (FastAPI serves the built FE in prod;
// Vite proxies in dev — see vite.config.ts).
// ============================================================================

export type ApiErrorKind =
  | 'not_found'        // 404 — design or project doesn't exist
  | 'forbidden'        // 403 — caller lacks access (DesignAccessDenied)
  | 'upstream'         // 502 — OCR or knowledge service down
  | 'unauthorized'     // 401 — no/invalid token (when auth lands)
  | 'validation'       // 422 — request body rejected by FastAPI
  | 'rate_limited'     // 429 — public calculator rate limit
  | 'server'           // 5xx other
  | 'network'          // fetch threw (offline, DNS, etc)
  | 'unknown';

export class ApiError extends Error {
  kind: ApiErrorKind;
  status: number;
  /** Optional FastAPI `detail` field, if parseable. */
  detail?: unknown;

  constructor(kind: ApiErrorKind, status: number, message: string, detail?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.detail = detail;
  }
}

function classify(status: number): ApiErrorKind {
  switch (status) {
    case 401: return 'unauthorized';
    case 403: return 'forbidden';
    case 404: return 'not_found';
    case 429: return 'rate_limited';
    case 422: return 'validation';
    case 502: return 'upstream';
    default:
      if (status >= 500) return 'server';
      return 'unknown';
  }
}

/**
 * Auth hook — when the backend exposes auth, set this to a function that
 * returns the bearer token. Until then it returns null and we send no header.
 */
let getAuthToken: () => string | null = () => null;

export function setAuthTokenProvider(fn: () => string | null) {
  getAuthToken = fn;
}

interface RequestOptions {
  signal?: AbortSignal;
  body?: unknown;
}

interface RawRequestOptions extends RequestOptions {
  accept?: string;
  auth?: boolean;
}

interface BlobRequestOptions extends RequestOptions {
  accept?: string;
}

async function requestRaw(
  method: 'GET' | 'POST',
  path: string,
  opts: RawRequestOptions = {},
): Promise<Response> {
  const headers: Record<string, string> = {
    Accept: opts.accept ?? 'application/json',
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  if (opts.auth !== false) {
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
      credentials: 'same-origin',
    });
  } catch (e) {
    throw new ApiError('network', 0, (e as Error).message ?? 'Network error');
  }

  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      // ignore — body wasn't JSON
    }
    throw new ApiError(classify(res.status), res.status, `HTTP ${res.status}`, detail);
  }

  return res;
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  opts: RawRequestOptions = {},
): Promise<T> {
  const res = await requestRaw(method, path, opts);
  return res.json() as Promise<T>;
}

export const http = {
  publicGet:  <T>(path: string, opts?: RequestOptions) =>
    request<T>('GET', path, { ...opts, auth: false }),
  publicPost: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, { ...opts, body, auth: false }),
  get:  <T>(path: string, opts?: RequestOptions) => request<T>('GET',  path, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, { ...opts, body }),
  blob: (path: string, opts?: BlobRequestOptions) =>
    requestRaw('GET', path, { ...opts, accept: opts?.accept ?? 'image/svg+xml' }).then(res => res.blob()),
};
